/**
 * 腾讯广告 → 策略配置（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；本页复用 leo-* 类）。
 *
 * 已读 docs/frontend_ui_guidline/leo-frontend-design.md v1.0（§6.3 开关 Toggle：药丸形、
 * `.is-on` 填充强调色；§6.1 按钮层级（主 CTA 橙渐变每区块唯一）；§6.3 表单控件
 * （输入深底 + focus 橙）与 checkbox 选中色 accent；§2.3 颜色使用规则）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/tencent/ad-operate-switch 拉取开关当前态；GET
 *     /api/v1/tencent/feilv-target-config 拉取目标费率配置（含链路近3/近7个工作日
 *     平均授信费率当前值 sxfl_per_3d / sxfl_per_7d 回显）。
 *   - 渲染「广告操作开关」药丸 Toggle：
 *       · 打开（is_on=true）：do_check 执行 update_ad（历史行为）。
 *       · 关闭（is_on=false）：do_check 不执行 update_ad，结果仍落表 status=operate_off。
 *   - 渲染「目标费率」卡片（auto_tx_feilv_target_config，替代 auto_aggr 硬编码 0.0165）：
 *       · 目标费率：[输入框] + 两个单选 checkbox（近3/近7个工作日链路平均费率）。
 *       · 未勾选（custom）：手动输入固定值（(0,1) 开区间小数，如 0.0165=1.65%）。
 *       · 勾选近3（sxfl_3d）/近7（sxfl_7d）：输入框只读回显链路平均费率当前值，
 *         决策时 do_check → account_aggr 按最新 statis 数据实时计算 feilv_target。
 *       · 再点已勾选项取消勾选，回到 custom 手动模式。
 *   - 权限：目标费率卡片独立权限键 tencent:feilv-target（devinzhang / v_yazhou /
 *     admin 可操作，广告操作开关仍为 tencent:strategy 仅 devinzhang / admin）；
 *     无权限用户控件 disabled 并提示只读。后端 PUT 同样校验（403/400/500 兜底）。
 *   - 审计：回显 modify_user · updated_time。
 *   - 飞书告警不受开关影响（详见后端 tx_alert.send_tx_ad_operate_alert）。
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type AdOperateSwitchRecord,
  getAdOperateSwitch,
  saveAdOperateSwitch,
} from "../../services/tencent/adOperateSwitch";
import {
  type FeilvMode,
  type FeilvTargetConfigRecord,
  getFeilvTargetConfig,
  saveFeilvTargetConfig,
} from "../../services/tencent/feilvTargetConfig";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_KEYS } from "../../config/permissions";
import { pushToast } from "../../hooks/useToast";

/** 链路平均费率当前值展示：无数据 / 解析失败统一「—」（LEO §7.3 空值规范）。 */
function fmtFeilv(value: number | undefined | null): string {
  return value != null ? String(value) : "—";
}

export default function StrategyConfigPage() {
  const { canEdit } = useAuth();
  // 广告操作开关：整页权限键 tencent:strategy（仅管理员 devinzhang / admin）。
  const canOperate = canEdit(PAGE_KEYS.TENCENT_STRATEGY);
  // 目标费率：独立权限键 tencent:feilv-target（devinzhang / v_yazhou / admin），
  // 与开关分离——v_yazhou 可改目标费率但不可切换广告操作开关。
  const canOperateFeilv = canEdit(PAGE_KEYS.TENCENT_FEILV_TARGET);

  const [record, setRecord] = useState<AdOperateSwitchRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 目标费率配置：record 为库内当前态；mode / valueDraft 为本地编辑态。
  const [feilvRecord, setFeilvRecord] = useState<FeilvTargetConfigRecord | null>(null);
  const [feilvMode, setFeilvMode] = useState<FeilvMode>("custom");
  const [feilvValueDraft, setFeilvValueDraft] = useState("0.0165");
  const [feilvSaving, setFeilvSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      try {
        setRecord(await getAdOperateSwitch());
      } catch (err) {
        pushToast(
          err instanceof ApiError ? err.message : "拉取广告操作开关失败",
          "error",
        );
        setRecord(null);
      }
      try {
        const r = await getFeilvTargetConfig();
        setFeilvRecord(r);
        if (r) {
          setFeilvMode(r.feilv_mode);
          setFeilvValueDraft(String(r.feilv_value));
        }
      } catch (err) {
        pushToast(
          err instanceof ApiError ? err.message : "拉取目标费率配置失败",
          "error",
        );
        setFeilvRecord(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onToggle() {
    if (!record || saving || !canOperate) return;
    const target = !record.is_on;
    setSaving(true);
    try {
      const r = await saveAdOperateSwitch({ is_on: target });
      setRecord(r);
      pushToast(
        target ? "广告操作开关已打开 · do_check 将执行 update_ad" : "广告操作开关已关闭 · do_check 不执行 update_ad（落表 operate_off）",
        "success",
      );
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "切换失败",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }

  /** 勾选/取消链路平均费率模式：单选（两 checkbox 互斥），再点已勾选项回到 custom。 */
  function onToggleFeilvMode(next: FeilvMode) {
    if (!canOperateFeilv || feilvSaving) return;
    setFeilvMode((m) => (m === next ? "custom" : next));
  }

  async function onSaveFeilv() {
    // feilvRecord 允许为 null（GET 失败/未加载时按默认基线 custom+0.0165 保存），
    // 不因读取失败永久锁死保存按钮。
    if (feilvSaving || !canOperateFeilv) return;
    const parsed = Number.parseFloat(feilvValueDraft);
    // custom 模式必须给出合法固定值；sxfl_* 模式下草稿非法时回落库内回显值
    if (feilvMode === "custom" && !(Number.isFinite(parsed) && parsed > 0 && parsed < 1)) {
      pushToast("目标费率须为 (0,1) 开区间小数（如 0.0165 表示 1.65%）", "error");
      return;
    }
    const customValue =
      Number.isFinite(parsed) && parsed > 0 && parsed < 1
        ? parsed
        : (feilvRecord?.feilv_value ?? 0.0165);
    setFeilvSaving(true);
    try {
      const r = await saveFeilvTargetConfig({
        feilv_mode: feilvMode,
        feilv_value: customValue,
      });
      setFeilvRecord(r);
      setFeilvMode(r.feilv_mode);
      setFeilvValueDraft(String(r.feilv_value));
      pushToast(
        feilvMode === "custom"
          ? `目标费率已保存为固定值 ${r.feilv_value}，下轮 do_check 决策起生效`
          : feilvMode === "sxfl_3d"
            ? "目标费率已改为跟随「近3个工作日链路平均费率」，下轮 do_check 决策起生效"
            : "目标费率已改为跟随「近7个工作日链路平均费率」，下轮 do_check 决策起生效",
        "success",
      );
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存目标费率配置失败",
        "error",
      );
    } finally {
      setFeilvSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  const isOn = record?.is_on ?? true;

  // ----- 目标费率派生态：输入框展示值 / 固定值合法性 / 是否有未保存修改 -----
  const parsedDraft = Number.parseFloat(feilvValueDraft);
  const isDraftValid =
    Number.isFinite(parsedDraft) && parsedDraft > 0 && parsedDraft < 1;
  // sxfl_* 模式下输入框只读回显链路平均费率当前值（无数据「—」）
  const feilvInputValue =
    feilvMode === "custom"
      ? feilvValueDraft
      : feilvMode === "sxfl_3d"
        ? fmtFeilv(feilvRecord?.sxfl_per_3d)
        : fmtFeilv(feilvRecord?.sxfl_per_7d);
  const feilvInvalid = feilvMode === "custom" && !isDraftValid;
  // 比对基线：库内态缺失（GET 失败/未加载）时按默认态 custom + 0.0165，
  // 保证「做修改后即可保存」不被读取失败锁死。
  const feilvBaselineMode: FeilvMode = feilvRecord?.feilv_mode ?? "custom";
  const feilvBaselineValue = feilvRecord?.feilv_value ?? 0.0165;
  const feilvDirty =
    feilvMode !== feilvBaselineMode ||
    (feilvMode === "custom" &&
      isDraftValid &&
      parsedDraft !== feilvBaselineValue);
  const feilvModeHint =
    feilvMode === "custom"
      ? "手动固定值模式：输入 (0,1) 开区间小数费率（如 0.0165 表示 1.65%）。"
      : feilvMode === "sxfl_3d"
        ? "已跟随「近3个工作日链路平均费率」：输入框为当前值回显（只读），每轮决策按最新数据实时计算。"
        : "已跟随「近7个工作日链路平均费率」：输入框为当前值回显（只读），每轮决策按最新数据实时计算。";

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5">
        <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
          策略配置
        </h1>
      </div>

      <div className="leo-card rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-display text-[1.05rem] font-semibold text-text-emphasis">
              广告操作开关
            </div>
            <div className="mt-1.5 text-[12.5px] text-text-muted">
              打开：do_check 正常执行 update_ad（控停/调控广告）。<br />
              关闭：do_check 不执行 update_ad，决策结果仍落表，status 固定
              <span className="font-mono"> operate_off</span>（仅告警类维持
              <span className="font-mono"> —</span>）。
            </div>
          </div>

          {/* 药丸 Toggle（LEO §6.3：药丸形，is-on 填充强调色橙）。
              轨道色主题感知：打开=橙 accent-cta（浅/深底均醒目）；关闭=text-muted-weak
              （浅色 slate-400 / 深色 slate-500，避免原 bg-white/10 在浅底近乎不可见）。
              白色 knob 在橙轨与灰轨上均有清晰对比。 */}
          <button
            type="button"
            role="switch"
            aria-checked={isOn}
            aria-label="广告操作开关"
            onClick={onToggle}
            disabled={!canOperate || saving}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 ${
              isOn ? "bg-accent-cta" : "bg-text-muted-weak"
            } ${!canOperate || saving ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-150 ${
                isOn ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* 审计行 */}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line-subtle pt-4 text-[12px] text-text-muted">
          {record?.modify_user ? (
            <span>
              最近修改：<span className="text-text-strong">{record.modify_user}</span>
              <span className="mx-1.5 text-text-muted-weak">·</span>
              <span className="font-mono">{record.updated_time ?? "—"}</span>
            </span>
          ) : (
            <span>尚无修改记录（默认打开）</span>
          )}
          {record?.create_user ? (
            <span>
              首次创建：<span className="text-text-strong">{record.create_user}</span>
            </span>
          ) : null}
        </div>

        {/* 权限提示 */}
        {!canOperate ? (
          <div className="mt-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
            仅 devinzhang / admin 可操作该开关；当前账号为只读。
          </div>
        ) : null}
      </div>

      {/* 目标费率卡片（LEO §6.3 表单控件：leo-input 深底 + focus 橙；checkbox 选中色
          accent 橙与既有 SpecialConditionEditor 一致；保存为本卡片唯一主 CTA §6.1）。 */}
      <div className="leo-card mt-5 rounded-xl p-6">
        <div className="min-w-0">
          <div className="font-display text-[1.05rem] font-semibold text-text-emphasis">
            目标费率
          </div>
          <div className="mt-1.5 text-[12.5px] text-text-muted">
            程序化控停策略的费率目标（feilv_target）。手动输入固定值；或勾选下方选项
            跟随链路平均费率（勾选后输入框回显当前值，每轮决策按最新数据实时计算）。
          </div>
        </div>

        {/* 「目标费率：[输入框]」固定同行：子组 shrink-0 内部不换行；输入框经定宽
            容器包裹——.leo-input 的 width:100% 在构建产物中位于 Tailwind w-* 之后
            （同优先级后者胜），直接设宽度会被撑满整行。 */}
        <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-2">
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="text-[12.5px] text-text-muted">目标费率：</span>
            <div className="w-28 shrink-0">
              <input
                type="text"
                inputMode="decimal"
                className="leo-input"
                value={feilvInputValue}
                placeholder="如 0.0165"
                readOnly={feilvMode !== "custom" || !canOperateFeilv}
                onChange={(e) => setFeilvValueDraft(e.target.value)}
                aria-label="目标费率"
              />
            </div>
          </div>
          <button
            type="button"
            className="leo-cta-primary ml-1 w-auto px-7"
            onClick={onSaveFeilv}
            disabled={!canOperateFeilv || feilvSaving || !feilvDirty || feilvInvalid}
          >
            {feilvSaving ? "保存中…" : "保存"}
          </button>
          {feilvInvalid ? (
            <span className="text-[11.5px] text-semantic-danger">
              须为 (0,1) 开区间小数（如 0.0165）
            </span>
          ) : null}
        </div>

        {/* 两个单选 checkbox（同一水平行）：勾选即切换 feilv_target 取值来源 */}
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
          <label
            className={`flex items-center gap-2 text-[12.5px] ${
              canOperateFeilv && !feilvSaving ? "cursor-pointer text-text-body" : "cursor-not-allowed text-text-muted"
            }`}
          >
            <input
              type="checkbox"
              className="accent-[#ff6b35]"
              checked={feilvMode === "sxfl_3d"}
              onChange={() => onToggleFeilvMode("sxfl_3d")}
              disabled={!canOperateFeilv || feilvSaving}
            />
            近3个工作日链路平均费率
            <span className="text-[11.5px] text-text-muted-weak">
              当前 {fmtFeilv(feilvRecord?.sxfl_per_3d)}
            </span>
          </label>
          <label
            className={`flex items-center gap-2 text-[12.5px] ${
              canOperateFeilv && !feilvSaving ? "cursor-pointer text-text-body" : "cursor-not-allowed text-text-muted"
            }`}
          >
            <input
              type="checkbox"
              className="accent-[#ff6b35]"
              checked={feilvMode === "sxfl_7d"}
              onChange={() => onToggleFeilvMode("sxfl_7d")}
              disabled={!canOperateFeilv || feilvSaving}
            />
            近7个工作日链路平均费率
            <span className="text-[11.5px] text-text-muted-weak">
              当前 {fmtFeilv(feilvRecord?.sxfl_per_7d)}
            </span>
          </label>
        </div>

        {/* 当前模式说明 */}
        <div className="mt-3 text-[11.5px] text-text-muted-weak">{feilvModeHint}</div>

        {/* 审计行 */}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line-subtle pt-4 text-[12px] text-text-muted">
          {feilvRecord?.modify_user ? (
            <span>
              最近修改：<span className="text-text-strong">{feilvRecord.modify_user}</span>
              <span className="mx-1.5 text-text-muted-weak">·</span>
              <span className="font-mono">{feilvRecord.updated_time ?? "—"}</span>
            </span>
          ) : (
            <span>尚无修改记录（默认 custom 0.0165）</span>
          )}
          {feilvRecord?.create_user ? (
            <span>
              首次创建：<span className="text-text-strong">{feilvRecord.create_user}</span>
            </span>
          ) : null}
        </div>

        {/* 权限提示 */}
        {!canOperateFeilv ? (
          <div className="mt-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
            仅 devinzhang / v_yazhou 可操作目标费率；当前账号为只读。
          </div>
        ) : null}
      </div>

      <div className="mt-4 text-[11.5px] text-text-muted-weak">
        提示：关闭开关为「干跑」观测模式——不实际改动广告，便于在低风险下观察策略决策与告警。
      </div>
    </div>
  );
}
